/**
 * ResocontoScommesse — chi ha scommesso cosa, giornata per giornata
 * (Andrea 2026-09-17). Lo vedono tutti.
 *
 * Per ogni partita di cartello della giornata: la sfida tra i due presidenti
 * e le puntate col banco, con la vincita possibile finche' si gioca e, a
 * giornata calcolata, VINTA / PERSA / rimborso (le scommesse le risolve il
 * calcolo della giornata). Sotto, il bilancio: della giornata o della
 * stagione.
 *
 * La pagina segue il campionato: si apre sulla prima giornata non ancora
 * calcolata (quella su cui si sta scommettendo o che si sta giocando) e,
 * quando non ce n'e', sull'ultima. Le frecce sfogliano le altre.
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LogoSquadra } from "../common/LogoSquadra";
import type {
  BilancioScommesseItem,
  EsitoPartita,
  ResocontoBancoItem,
  ResocontoGiornataItem,
  ResocontoP2PItem,
  ResocontoPartitaItem,
  ResocontoScommesse as Resoconto,
} from "../../hooks/useScommesse";
import { tr } from "../../i18n";

/** "3M", "8.50M": senza decimali inutili. */
function mln(v: string | number): string {
  const n = Number(v);
  return tr("{{v1}}M", { v1: Number.isInteger(n) ? n : n.toFixed(2).replace(/0$/, "") });
}

function mlnSegno(v: string | number): string {
  const n = Number(v);
  if (Math.abs(n) < 0.005) return "0M";
  return tr("{{v1}}{{v2}}", { v1: n > 0 ? "+" : "−", v2: mln(Math.abs(n)) });
}

const PRONOSTICO: Record<EsitoPartita, string> = { "1": "1", x: "X", "2": "2" };

/** La giornata su cui aprire: la prima non ancora calcolata, o l'ultima. */
function giornataDiDefault(giornate: ResocontoGiornataItem[]): number {
  const i = giornate.findIndex((g) => g.stato !== "calcolata" && g.stato !== "annullata");
  return i >= 0 ? i : giornate.length - 1;
}

function statoGiornata(g: ResocontoGiornataItem): { label: string; cls: string } {
  if (g.stato === "calcolata") return { label: tr("scommesse risolte"), cls: "text-success" };
  if (g.chiusa) return { label: tr("si gioca · scommesse chiuse"), cls: "text-warning" };
  return { label: tr("scommesse aperte"), cls: "text-accent" };
}

function Pastiglia({
  tono,
  children,
}: {
  tono: "vinta" | "persa" | "neutro" | "gioco";
  children: React.ReactNode;
}) {
  const cls = {
    vinta: "bg-success/15 text-success border-success/40",
    persa: "bg-danger/15 text-danger border-danger/40",
    neutro: "bg-primary-deep/40 text-fg-muted border-primary-soft/50",
    gioco: "bg-accent/15 text-accent border-accent/40",
  }[tono];
  return (
    <span
      className={`shrink-0 inline-flex items-center rounded px-1.5 py-0.5 text-[9px] font-mono font-bold uppercase tracking-wide border ${cls}`}
    >
      {children}
    </span>
  );
}

function RigaSfida({ s, nome }: { s: ResocontoP2PItem; nome: (id: string) => string }) {
  const proponente = nome(s.proponente_squadra_id);
  const accettante = nome(s.accettante_squadra_id);
  let testo: React.ReactNode;
  let pastiglia: React.ReactNode;
  if (s.stato === "proposta") {
    testo = (
      <>
        <b>{proponente}</b> {tr("sfida")} <b>{accettante}</b>: {mln(s.importo_milioni)} a testa
      </>
    );
    pastiglia = <Pastiglia tono="neutro">{tr("in attesa di risposta")}</Pastiglia>;
  } else if (s.stato === "accettata") {
    testo = (
      <>
        <b>{proponente}</b> {tr("contro")} <b>{accettante}</b>: {mln(s.importo_milioni)} a testa ·
        chi vince prende <b className="text-success">{mln(s.piatto)}</b>
      </>
    );
    pastiglia = <Pastiglia tono="gioco">{tr("in gioco")}</Pastiglia>;
  } else if (s.stato === "risolta" && s.esito === "pareggio_rimborso") {
    testo = (
      <>
        <b>{proponente}</b> {tr("contro")} <b>{accettante}</b>: {mln(s.importo_milioni)} a testa
      </>
    );
    pastiglia = <Pastiglia tono="neutro">{tr("pareggio · rimborso")}</Pastiglia>;
  } else if (s.stato === "risolta" && s.vincitore_squadra_id) {
    const sconfitto =
      s.vincitore_squadra_id === s.proponente_squadra_id
        ? s.accettante_squadra_id
        : s.proponente_squadra_id;
    testo = (
      <>
        <b className="text-success">{nome(s.vincitore_squadra_id)}</b> vince la sfida con{" "}
        <b>{nome(sconfitto)}</b> e incassa {mln(s.piatto)}{" "}
        <span className="text-fg-muted">
          ({mlnSegno(s.importo_milioni)} / {mlnSegno(-Number(s.importo_milioni))})
        </span>
      </>
    );
    pastiglia = <Pastiglia tono="vinta">vinta da {nome(s.vincitore_squadra_id)}</Pastiglia>;
  } else {
    testo = (
      <span className="text-fg-muted">
        {proponente} aveva sfidato {accettante} per {mln(s.importo_milioni)}
      </span>
    );
    pastiglia = (
      <Pastiglia tono="neutro">
        {s.stato === "rifiutata" ? tr("rifiutata · rimborso") : tr("annullata · rimborso")}
      </Pastiglia>
    );
  }
  return (
    <div className="flex items-start justify-between gap-2 text-[12px] leading-snug">
      <div className="min-w-0">{testo}</div>
      {pastiglia}
    </div>
  );
}

function RigaBanco({ b, nome }: { b: ResocontoBancoItem; nome: (id: string) => string }) {
  return (
    <div className="flex items-start justify-between gap-2 text-[12px] leading-snug">
      <div className="min-w-0 flex items-center gap-1.5 flex-wrap">
        <LogoSquadra squadraId={b.squadra_id} size={18} />
        <b className="truncate">{nome(b.squadra_id)}</b>
        <span className="text-fg-muted">
          {mln(b.importo_milioni)} su{" "}
          <span className="font-mono font-bold text-fg">{PRONOSTICO[b.pronostico]}</span> @{" "}
          {b.quota_applicata}
        </span>
        {b.stato === "piazzata" && (
          <span className="text-fg-muted">
            {tr("→ può vincere")} <b className="text-success">{mln(b.vincita_potenziale)}</b>
          </span>
        )}
      </div>
      {b.stato === "vinta" && <Pastiglia tono="vinta">vinta {mlnSegno(b.netto ?? 0)}</Pastiglia>}
      {b.stato === "persa" && <Pastiglia tono="persa">persa {mlnSegno(b.netto ?? 0)}</Pastiglia>}
      {b.stato === "piazzata" && <Pastiglia tono="gioco">{tr("in gioco")}</Pastiglia>}
      {b.stato === "annullata" && <Pastiglia tono="neutro">{tr("annullata")}</Pastiglia>}
    </div>
  );
}

function PartitaCard({ p, nome }: { p: ResocontoPartitaItem; nome: (id: string) => string }) {
  const giocata = p.gol_casa !== null && p.gol_trasferta !== null;
  const quote: Array<[EsitoPartita, string | null]> = [
    ["1", p.quota_1],
    ["x", p.quota_x],
    ["2", p.quota_2],
  ];
  const nessuna = p.p2p.length === 0 && p.banco.length === 0;
  return (
    <div className="bg-bg-card border border-primary/20 rounded-card p-3 space-y-2">
      <Link
        to={`/partita/${p.partita_id}`}
        className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 hover:opacity-90"
        title={tr("Apri la partita")}
      >
        <div className="flex items-center gap-1.5 min-w-0">
          <LogoSquadra squadraId={p.squadra_casa_id} size={26} />
          <span className="font-display text-sm truncate">{nome(p.squadra_casa_id)}</span>
        </div>
        <div className="text-center">
          <div className="font-display text-lg tabular-nums leading-none">
            {giocata ? `${p.gol_casa} - ${p.gol_trasferta}` : "vs"}
          </div>
          {p.esito && (
            <div className="text-[9px] font-mono font-bold text-info uppercase mt-0.5">
              esito {PRONOSTICO[p.esito]}
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-1.5 min-w-0">
          <span className="font-display text-sm truncate text-right">
            {nome(p.squadra_trasferta_id)}
          </span>
          <LogoSquadra squadraId={p.squadra_trasferta_id} size={26} />
        </div>
      </Link>
      {/* Christian 2026-10-30: la competizione resta visibile anche a
          scommesse chiuse. */}
      {p.competizione_nome && (
        <div className="text-center text-[10px] text-fg-muted font-mono -mt-1">
          {p.competizione_tipo === "campionato" ? "📊" : "🏆"} {p.competizione_nome}
        </div>
      )}

      {p.banco_attivo && (
        <div className="flex items-center justify-center gap-1.5 text-[10px] font-mono text-fg-muted">
          <span className="uppercase tracking-wider">{tr("quote")}</span>
          {quote.map(([k, q]) => (
            <span
              key={k}
              className={`rounded px-1.5 py-0.5 border tabular-nums ${
                p.esito === k
                  ? "border-success/50 bg-success/15 text-success font-bold"
                  : "border-primary-soft/50 bg-primary-deep/30"
              }`}
            >
              {PRONOSTICO[k]} {q ?? "—"}
            </span>
          ))}
        </div>
      )}

      {p.p2p.length > 0 && (
        <div className="border-t border-primary/10 pt-2 space-y-1.5">
          <div className="font-mono text-[10px] uppercase tracking-wider text-accent">
            {tr("Sfida tra presidenti")}
          </div>
          {p.p2p.map((s) => (
            <RigaSfida key={s.id} s={s} nome={nome} />
          ))}
        </div>
      )}

      {p.banco.length > 0 && (
        <div className="border-t border-primary/10 pt-2 space-y-1.5">
          <div className="font-mono text-[10px] uppercase tracking-wider text-accent">
            {tr("Puntate col banco")}
          </div>
          {p.banco.map((b) => (
            <RigaBanco key={b.id} b={b} nome={nome} />
          ))}
        </div>
      )}

      {nessuna && (
        <div className="border-t border-primary/10 pt-2 text-[11px] text-fg-muted text-center">
          {tr("Nessuno ha scommesso su questa partita.")}
        </div>
      )}
    </div>
  );
}

function TabellaBilancio({
  righe,
  nome,
}: {
  righe: BilancioScommesseItem[];
  nome: (id: string) => string;
}) {
  if (righe.length === 0) {
    return (
      <div className="text-[11px] text-fg-muted text-center py-3">
        {tr("Ancora nessuna scommessa.")}
      </div>
    );
  }
  const GRID = "grid grid-cols-[1fr_3rem_3.4rem_3.6rem_3.6rem] gap-1 px-2";
  return (
    <div className="bg-bg-card border border-primary/20 rounded-card overflow-hidden">
      <div
        className={`${GRID} py-1.5 bg-primary-deep/40 text-[9px] text-fg-muted uppercase tracking-wider font-mono`}
      >
        <div>{tr("Squadra")}</div>
        <div className="text-right" title={tr("Scommesse vinte / perse")}>
          V/P
        </div>
        <div className="text-right" title={tr("Puntato nelle scommesse chiuse")}>
          {tr("Punt.")}
        </div>
        <div className="text-right" title={tr("Ancora in gioco")}>
          {tr("In gioco")}
        </div>
        <div className="text-right" title={tr("Incassato meno puntato, sulle scommesse chiuse")}>
          {tr("Netto")}
        </div>
      </div>
      {righe.map((r) => {
        const netto = Number(r.netto);
        return (
          <div
            key={r.squadra_id}
            className={`${GRID} py-1.5 text-[12px] border-t border-primary/10 items-center`}
          >
            <div className="flex items-center gap-1.5 min-w-0">
              <LogoSquadra squadraId={r.squadra_id} size={18} />
              <span className="truncate">{nome(r.squadra_id)}</span>
            </div>
            <div className="text-right font-mono tabular-nums">
              <span className="text-success">{r.vinte}</span>/
              <span className="text-danger">{r.perse}</span>
            </div>
            <div className="text-right font-mono tabular-nums text-fg-muted">
              {Number(r.puntato) > 0 ? mln(r.puntato) : "—"}
            </div>
            <div className="text-right font-mono tabular-nums text-accent">
              {Number(r.in_gioco) > 0 ? mln(r.in_gioco) : "—"}
            </div>
            <div
              className={`text-right font-mono tabular-nums font-bold ${
                netto > 0 ? "text-success" : netto < 0 ? "text-danger" : "text-fg-muted"
              }`}
            >
              {mlnSegno(netto)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function ResocontoScommesse({ resoconto }: { resoconto: Resoconto }) {
  const { giornate } = resoconto;
  const [indice, setIndice] = useState(() => giornataDiDefault(giornate));
  // La pagina segue il campionato: quando cambia la giornata "di adesso"
  // (una viene calcolata, se ne apre un'altra) ci si sposta su quella.
  const chiaveDefault = giornate[giornataDiDefault(giornate)]?.giornata_id ?? null;
  useEffect(() => {
    setIndice(giornataDiDefault(giornate));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chiaveDefault, giornate.length]);
  const [vistaBilancio, setVistaBilancio] = useState<"giornata" | "stagione">("giornata");

  const nome = (id: string): string => resoconto.nomi_squadre[id] ?? id.slice(0, 8);
  const g = giornate[indice] ?? null;
  if (!g) return null;
  const stato = statoGiornata(g);
  const inGioco = g.bilancio.reduce((t, r) => t + Number(r.in_gioco), 0);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-display text-lg tracking-wider text-fg">{tr("RESOCONTO")}</h3>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => setIndice((i) => Math.max(0, i - 1))}
            disabled={indice === 0}
            aria-label={tr("Giornata precedente")}
            className="w-7 h-7 rounded-full bg-primary-deep/85 border border-accent/40 text-accent leading-none flex items-center justify-center hover:bg-primary-deep transition disabled:opacity-30"
          >
            ‹
          </button>
          <span className="font-mono text-xs uppercase tracking-wider text-fg min-w-[6.5rem] text-center">
            Giornata {g.numero}
            <span className="text-fg-muted"> · SA {g.numero_serie_a}</span>
          </span>
          <button
            type="button"
            onClick={() => setIndice((i) => Math.min(giornate.length - 1, i + 1))}
            disabled={indice === giornate.length - 1}
            aria-label={tr("Giornata successiva")}
            className="w-7 h-7 rounded-full bg-primary-deep/85 border border-accent/40 text-accent leading-none flex items-center justify-center hover:bg-primary-deep transition disabled:opacity-30"
          >
            ›
          </button>
        </div>
      </div>
      <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-wider">
        <span className={stato.cls}>{stato.label}</span>
        {inGioco > 0 && <span className="text-fg-muted">in gioco {mln(inGioco)}</span>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {g.partite.map((p) => (
          <PartitaCard key={p.cartello_id} p={p} nome={nome} />
        ))}
      </div>

      <div className="flex items-center justify-between gap-2 pt-1">
        <h4 className="font-display text-base tracking-wider text-fg">{tr("BILANCIO")}</h4>
        <div className="flex gap-1">
          {(["giornata", "stagione"] as const).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setVistaBilancio(v)}
              className={`pill text-[10px] ${
                vistaBilancio === v
                  ? "bg-accent text-ink"
                  : "bg-primary-deep/40 text-fg-muted hover:bg-primary-deep/60"
              }`}
            >
              {v === "giornata" ? tr("Giornata {{v1}}", { v1: g.numero }) : tr("Stagione")}
            </button>
          ))}
        </div>
      </div>
      <TabellaBilancio
        righe={vistaBilancio === "giornata" ? g.bilancio : resoconto.bilancio}
        nome={nome}
      />
      <p className="text-[10px] text-fg-muted">
        Netto = incassato meno puntato sulle scommesse chiuse. Le scommesse si risolvono da sole
        quando la giornata viene calcolata; nella sfida tra presidenti il pareggio rimborsa
        entrambi.
      </p>
    </div>
  );
}
